import { getHexTileMapKey } from '../hexmap/model/keys';
import type { RuleSet } from '../rules/schema';
import {
  type AssignRejection,
  type BlendRejection,
  type Building,
  type BuildingType,
  buildingLevel,
  cityStores,
  type EntityId,
  type FoundCityCheck,
  holdsStock,
  levelDef,
  levelTitle,
  MAX_TRAINING_QUEUE,
  maxLevel,
  minCityDistance,
  type PlaceRejection,
  type PlayerId,
  type PromoteRejection,
  type SimState,
  SPAWN_RADIUS,
  type SpawnRejection,
  storageCapacity,
  storesType,
  type TrainRejection,
  territoryOwners,
  trainCost,
  type Unit,
  type UnitType,
  type UpgradeCheck,
  unitCap,
  unitCost,
  unitRank,
  upgradeOf,
} from '../sim';
import { formatAmounts, orderedResources, resourceIcon, stockInUnits } from './resources';

/**
 * Player-facing text for why an action can't happen, shared by disabled
 * buttons and the toast shown when the sim refuses a click. Pure (no React).
 */

/** "90🌾 180🪵": a cost as shown on Train and Level up buttons. */
export function costLabel(cost: Record<string, number>): string {
  return formatAmounts(cost, { compact: true });
}

export function missingLabel(
  stockpile: Record<string, number>,
  cost: Record<string, number>
): string | null {
  const parts: string[] = [];
  for (const res of orderedResources(cost)) {
    const amount = cost[res] ?? 0;
    // Floor the held amount so the shortfall matches the floored resource
    // readout instead of leaking fractional stockpile ("Need 1.6 more").
    const short = amount - Math.floor(stockpile[res] ?? 0);
    if (short > 0) {
      parts.push(`${short} more ${resourceIcon(res)}`);
    }
  }
  if (parts.length === 0) return null;
  return `Need ${parts.join(' + ')}`;
}

/** How tile types read in placement hints ("Farms go on fields (wheat)"). */
const PLACE_TILE_NAMES: Record<string, string> = {
  field: 'fields (wheat)',
  forest: 'forests',
  default: 'grass',
  beach: 'beaches',
  water: 'water',
};

/** "Farms", "Forestries", "Barracks". */
function plural(name: string): string {
  if (name.endsWith('s')) return name;
  return name.endsWith('y') ? `${name.slice(0, -1)}ies` : `${name}s`;
}

/** "Farms go on fields (wheat) in the city's territory" (`city`: "in City 2's territory"). */
export function placementHint(rules: RuleSet, type: BuildingType, city?: string): string {
  const def = rules.buildings[type];
  const on = (def?.placement?.on ?? []).map((t) => PLACE_TILE_NAMES[t] ?? t).join(' or ');
  return `${plural(def?.name ?? type)} go on ${on} in ${city ? `${city}'s` : "the city's"} territory`;
}

/** A refused placement: the tile clicked (null: every tile, e.g. the city's era) and why. */
export interface PlacementRefusal {
  key: string | null;
  text: string;
}

/**
 * The placement banner's one message: the refusal while the cursor is still
 * on the refused tile (or off the map), else the generic hint, never both.
 */
export function placementBanner(
  hint: string,
  refusal: PlacementRefusal | null,
  hoveredKey: string | null
): { text: string; refused: boolean } {
  if (refusal && (refusal.key === null || hoveredKey === null || hoveredKey === refusal.key)) {
    return { text: refusal.text, refused: true };
  }
  return { text: `${hint} — shift-click places more, Esc/right-click cancels`, refused: false };
}

/** What a placement refusal knows about the clicked tile, for a specific reason. */
export interface PlaceRefusalDetails {
  /** The clicked tile's type ("grass"), for `tile-type`. */
  tileType?: string;
  /** The city being built from ("City 2"), for `territory` and `limit`. */
  city?: string;
}

/**
 * Why a site can't go on a tile, for that tile ("Farms go on fields (wheat),
 * not grass"); without `details`, the general placement rule. `unexplored`
 * is the UI's own refusal for a tile under the fog.
 */
export function placeRejectionText(
  rules: RuleSet,
  type: BuildingType,
  reason: PlaceRejection | 'unexplored',
  stockpile: Record<string, number>,
  details: PlaceRefusalDetails = {}
): string {
  const name = rules.buildings[type]?.name ?? type;
  switch (reason) {
    case 'unexplored':
      return "You haven't explored that tile";
    case 'tile-type': {
      const on = (rules.buildings[type]?.placement?.on ?? [])
        .map((t) => PLACE_TILE_NAMES[t] ?? t)
        .join(' or ');
      return details.tileType
        ? `${plural(name)} go on ${on}, not ${PLACE_TILE_NAMES[details.tileType] ?? details.tileType}`
        : placementHint(rules, type);
    }
    case 'territory':
      return details.city ? `Outside ${details.city}'s territory` : placementHint(rules, type);
    case 'era':
      return details.city
        ? `${plural(name)} need ${cityAtLevelText(rules, details.city, buildingCityLevel(rules, type))}`
        : `${plural(name)} need ${cityLevelText(rules, buildingCityLevel(rules, type))}`;
    case 'occupied':
      return 'That tile already has a building';
    case 'limit':
      return `${details.city ?? 'This city'} already has its ${name} (one per city)`;
    case 'cost':
      return missingLabel(stockpile, rules.buildings[type]?.cost ?? {}) ?? 'Not enough resources';
    default:
      return "Can't build there";
  }
}

/**
 * "Barracks full (8/8)" when city `cityId` has stores that take `unitType`
 * (a barracks for soldiers, a factory for vehicles) and none of them has room
 * (counts summed over them); null when one has room or the city has none. A
 * trained unit then waits outside instead of inside.
 */
export function barracksFullText(
  state: SimState,
  rules: RuleSet,
  cityId: EntityId,
  unitType: UnitType
): string | null {
  const stores = cityStores(state, rules, cityId, territoryOwners(state, rules)).filter((b) =>
    storesType(rules, b, unitType)
  );
  if (stores.length === 0) return null;
  let used = 0;
  let capacity = 0;
  for (const b of stores) {
    const n = Object.values(state.units).filter((u) => u.inside === b.id).length;
    const room = storageCapacity(rules, b);
    if (n < room) return null;
    used += n;
    capacity += room;
  }
  const name = rules.buildings[stores[0].type]?.name ?? stores[0].type;
  return `${name} full (${used}/${capacity})`;
}

/**
 * A unit type the player has more of than its cap allows: caps come from
 * their cities, so only losing one gets them there. Says so instead of a bare
 * "44/26" (short for a Train card; `title` in full). Null while within the cap.
 */
export function overCapText(count: number, cap: number): { short: string; title: string } | null {
  if (!(count > cap)) return null;
  return {
    short: 'Over cap: lost a city',
    title: `Over the cap (${count}/${cap}): caps come from your cities and you lost one. You keep these units, but can't train more until you're under it`,
  };
}

/**
 * `cityId` (the city training) lets a refusal for space say how full its
 * barracks are; `city` ("City 2") names where a missing building must stand
 * ("Needs a Barracks in City 2's territory"): unlocks are per city.
 */
export function spawnRejectionText(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  unitType: UnitType,
  reason: SpawnRejection,
  cityId?: EntityId,
  city?: string
): string {
  const def = rules.units[unitType];
  switch (reason) {
    case 'locked': {
      const req = def?.requires;
      if (!req) return "Can't train here";
      return city
        ? `Needs ${requiredBuildingText(rules, req.building, req.level)} in ${city}'s territory`
        : `Needs ${rules.buildings[req.building]?.name ?? req.building} ${req.level}`;
    }
    case 'cap':
      return `Limit reached (${unitCap(state, rules, playerId, unitType)})`;
    case 'queue':
      return `Training queue full (${MAX_TRAINING_QUEUE})`;
    case 'cost':
      return (
        missingLabel(stockInUnits(state, playerId), unitCost(state, rules, playerId, unitType)) ??
        'Not enough resources'
      );
    case 'space': {
      const full =
        def?.military && cityId ? barracksFullText(state, rules, cityId, unitType) : null;
      const room = `no free tile within ${SPAWN_RADIUS} of the city`;
      return full ? `${full} and ${room}` : `No free tile within ${SPAWN_RADIUS} of the city`;
    }
    default:
      return "Can't train here";
  }
}

/**
 * Why a settler can't found here. `cityName` names the city in the way
 * ("City 2", "p2's city"); without it, or when it returns null, "a city".
 */
export function foundCityRejectionText(
  rules: RuleSet,
  check: Extract<FoundCityCheck, { ok: false }>,
  cityName?: (cityId: string) => string | null
): string {
  switch (check.reason) {
    case 'too-close': {
      const name = cityName?.(check.cityId) ?? 'a city';
      const min = minCityDistance(rules);
      return `Too close to ${name} (${check.distance} ${check.distance === 1 ? 'tile' : 'tiles'}; needs ${min})`;
    }
    case 'occupied':
      return 'This tile already has a building';
    default:
      return 'The settler is gone';
  }
}

/**
 * Why a spy can't blend in where it stands (sim `canBlend`). `exposed`,
 * `territory` and `occupied` are the ones a player meets; the rest read as a
 * generic no.
 */
/** Why `assign` sent no worker (sim assign.ts): shown as "Couldn't send workers: …". */
export function assignRejectionText(reason: AssignRejection): string {
  switch (reason) {
    case 'unexplored':
      return "that tile isn't explored yet";
    case 'rival':
      return "that building isn't yours";
    case 'cantWork':
      return "these units can't do that work";
    default:
      return 'nothing to work there';
  }
}

/**
 * Why a task's target can't go up (`setWorkTargets`, sim workTargets.ts): the
 * targets already name every worker the city supports, and the sim would trim
 * the rest.
 */
export function workTargetsCapText(cap: number): string {
  return `Targets already use all ${cap} workers this city supports: lower another task first`;
}

export function blendRejectionText(reason: BlendRejection): string {
  switch (reason) {
    case 'exposed':
      return 'Exposed: it can no longer blend in';
    case 'territory':
      return "Move it into a rival city's territory to blend in";
    case 'occupied':
      return 'Another unit stands here: move it to a free tile to blend in';
    case 'resource':
      // Gathering a tile that never runs down would give it away.
      return 'Stand on grass, beach or a used-up tile to blend in';
    default:
      return "Can't blend in";
  }
}

/** The toast when an `expose` picked a unit that wasn't a disguised spy. */
export function wrongExposeText(rules: RuleSet): string {
  return `Not a spy — you can't expose again for ${rules.pacing.exposeCooldown} s`;
}

/**
 * Fog-safe reason for a founding the sim refused though the player's view
 * allowed it: a city they can't see is too close. Says only that one is
 * within range, never where or whose.
 */
export function hiddenCityText(rules: RuleSet): string {
  const min = minCityDistance(rules);
  return `A city is less than ${min} tiles away`;
}

/** `cityName` names the building's city ("City 2") in an era refusal. */
export function upgradeRejectionText(
  state: SimState,
  rules: RuleSet,
  building: Building,
  playerId: PlayerId,
  check: Extract<UpgradeCheck, { ok: false }>,
  cityName?: (city: Building) => string
): string | null {
  const def = rules.buildings[building.type];
  const level = buildingLevel(building);
  switch (check.reason) {
    case 'max-level':
      return 'Max level';
    case 'cost': {
      const next = def && levelDef(def, level + 1);
      return next ? missingLabel(stockInUnits(state, playerId), next.cost) : 'Not enough resources';
    }
    case 'city-level': {
      const city =
        state.buildings[territoryOwners(state, rules).get(getHexTileMapKey(building.pos)) ?? ''];
      if (city?.owner !== playerId) return 'Outside your territory';
      return cityName
        ? `Needs ${cityAtLevelText(rules, cityName(city), check.cityLevel)}`
        : `Needs ${cityLevelText(rules, check.cityLevel)}`;
    }
    case 'upgrading':
      return 'Already upgrading';
    case 'under-attack':
      return "Under attack: wait until it's safe";
    default:
      return null;
  }
}

/**
 * The name of the building that stores (and so trains) `unitType`: one whose
 * `stores` lists it, else one with `capacity` and no `stores` list.
 */
function trainingStoreName(rules: RuleSet, unitType: UnitType): string {
  const defs = Object.values(rules.buildings).filter((b) => b.capacity);
  const def = defs.find((b) => b.stores?.includes(unitType)) ?? defs.find((b) => !b.stores);
  return def?.name ?? 'Barracks';
}

export function trainRejectionText(
  state: SimState,
  rules: RuleSet,
  unit: Unit,
  reason: TrainRejection
): string {
  const next = unitRank(rules, unit) + 1;
  const store = trainingStoreName(rules, unit.type);
  switch (reason) {
    case 'no-barracks':
      return `Must be in the territory of your city with a ${store}`;
    case 'max-rank':
      return 'Top rank';
    case 'barracks-level':
      return `Rank ${next} needs ${store} level ${next}`;
    case 'cost':
      return (
        missingLabel(stockInUnits(state, unit.owner), trainCost(rules, next)) ??
        'Not enough resources'
      );
    default:
      return "Can't rank up this unit";
  }
}

/** "Armoury (Barracks 4)", or "Barracks 2" when that level has no title of its own. */
function buildingAtLevelText(rules: RuleSet, type: BuildingType, level: number): string {
  const def = rules.buildings[type];
  if (!def) return `${type} ${level}`;
  const title = levelTitle(def, level);
  return title === def.name ? `${def.name} ${level}` : `${title} (${def.name} ${level})`;
}

/**
 * Why `unit` can't be promoted into `to` (`canPromote`'s reason): for
 * `where`, the building the upgrade needs in the territory of the city it
 * stands in ("Needs Armoury (Barracks 4) in this city's territory"), or that
 * it must stand in an own city's territory at all.
 */
export function promoteRejectionText(
  state: SimState,
  rules: RuleSet,
  unit: Unit,
  to: UnitType,
  reason: PromoteRejection,
  owners?: ReadonlyMap<string, EntityId>
): string {
  const toName = rules.units[to]?.name ?? to;
  const upgrade = upgradeOf(rules, unit.type, to);
  switch (reason) {
    case 'no-path':
      return `${rules.units[unit.type]?.name ?? unit.type} can't become ${toName}`;
    case 'where': {
      const at = upgrade?.at;
      const city =
        state.buildings[
          (owners ?? territoryOwners(state, rules)).get(getHexTileMapKey(unit.pos)) ?? ''
        ];
      const inCity = !!city && city.owner === unit.owner && holdsStock(rules, city);
      const needs = at ? buildingAtLevelText(rules, at.building, at.level) : null;
      if (inCity && needs) return `Needs ${needs} in this city's territory`;
      return needs
        ? `Must be in the territory of your city with ${needs}`
        : "Must be in your city's territory";
    }
    case 'cost':
      return (
        (upgrade && missingLabel(stockInUnits(state, unit.owner), upgrade.cost)) ??
        'Not enough resources'
      );
    case 'cap':
      return `${toName} limit reached (${unitCap(state, rules, unit.owner, to)})`;
    default:
      return 'The unit is gone';
  }
}

/** "an Industrial city (level 5)": a city level by its title (a city's era). */
export function cityLevelText(rules: RuleSet, level: number): string {
  const city = rules.buildings.city;
  const title = city ? levelTitle(city, level) : 'City';
  const article = /^[aeiou]/i.test(title) ? 'an' : 'a';
  return `${article} ${title} city (level ${level})`;
}

/** "City 2 at Industrial (level 5)": a named city reaching a level (its era). */
export function cityAtLevelText(rules: RuleSet, city: string, level: number): string {
  const def = rules.buildings.city;
  const title = def ? levelTitle(def, level) : 'City';
  return title === def?.name ? `${city} at level ${level}` : `${city} at ${title} (level ${level})`;
}

/** "a Barracks" at level 1, "Barracks 2" (or a titled level, "Armoury (Barracks 4)") above. */
function requiredBuildingText(rules: RuleSet, type: BuildingType, level: number): string {
  if (level > 1) return buildingAtLevelText(rules, type, level);
  const name = rules.buildings[type]?.name ?? type;
  return `${/^[aeiou]/i.test(name) ? 'an' : 'a'} ${name}`;
}

/**
 * The city level a player's city needs for building `type` at `level` (1:
 * to place it) set by era unlocks: its `placement.cityLevel` and the
 * `cityLevel` of each level up to `level` (they're climbed in order). 1 when
 * nothing asks for more; the "can't pass its city's level" rule isn't an era.
 */
export function buildingCityLevel(rules: RuleSet, type: BuildingType, level = 1): number {
  const def = rules.buildings[type];
  if (!def) return 1;
  let needed = def.placement?.cityLevel ?? 1;
  for (let l = 2; l <= level; l++) needed = Math.max(needed, levelDef(def, l)?.cityLevel ?? 1);
  return needed;
}

/** The city level unit `type` needs through its `requires` building (1 when none). */
export function unitCityLevel(rules: RuleSet, type: UnitType): number {
  const req = rules.units[type]?.requires;
  return req ? buildingCityLevel(rules, req.building, req.level) : 1;
}

/**
 * What city level `level` unlocks, by display name: buildings placed from
 * there ("Mine"), building levels it opens ("Farm L4 (Mechanised)") and units
 * first trainable there.
 */
export function cityUnlocks(rules: RuleSet, level: number): string[] {
  const out: string[] = [];
  for (const def of Object.values(rules.buildings)) {
    if (def.placement?.cityLevel === level) out.push(def.name);
    for (let l = 2; l <= maxLevel(def); l++) {
      if (levelDef(def, l)?.cityLevel !== level) continue;
      const title = levelTitle(def, l);
      out.push(`${def.name} L${l}${title === def.name ? '' : ` (${title})`}`);
    }
  }
  for (const [type, def] of Object.entries(rules.units)) {
    if (level > 1 && unitCityLevel(rules, type) === level) out.push(def.name);
  }
  return out;
}
